import { observable, computed, when, action, runInAction, reaction, asMap } from 'mobx'

import { getKeywordAnalysis, getKeywordHints } from 'transport/keyword'
import { getHints as getFacultyHints } from 'transport/faculty'

import UiStore from 'stores/UiStore'

import FacultyModel from 'models/FacultyModel'

import { debounce } from 'utils'

class ExpertiseStore {
  @observable fullFaculties = asMap({})

  @observable facultyMap = asMap({})

  @observable selectedKeywordId = null

  @observable keywordAnalysis = null

  clearKeywordAnalysis () {
    this.keywordAnalysis = null
  }

  @observable keywordHints = []

  // This can't be computed since react-autosuggest manages keywordHints and
  // may clear it to an empty array.
  @observable exactKeywordNotFound = false

  @observable facultyHints = []

  @computed get suggestions () {
    let suggestions = []
    if (this.facultyHints.length > 0) {
      suggestions.push({ title: 'faculty', suggestions: this.facultyHints })
    }
    if (this.keywordHints.length > 0) {
      suggestions.push({ title: 'keywords', suggestions: this.keywordHints })
    }
    return suggestions
  }

  @observable displayResults = false

  @action async fetchKeywordHints (callback) {
    if (UiStore.expertiseFilters.search === '') {
      this.keywordHints = []
      return
    }

    const response = await getKeywordHints(UiStore.expertiseFilters.search)
    runInAction(() => {
      this.keywordHints = response
      this.exactKeywordNotFound = !response.find(k =>
        k.keyword.toLowerCase() === UiStore.expertiseFilters.search.toLowerCase())

      if (callback !== undefined) {
        callback(this.keywordHints)
      }
    })
  }

  @action async fetchKeywordAnalysis () {
    // clear the canvas when loading new graph
    this.keywordAnalysis = null
    const response = await getKeywordAnalysis(this.selectedKeywordId)
    runInAction(() => {
      this.keywordAnalysis = response
      this.keywordAnalysis.authors = response.authors.reduce((acc, faculty) => {
        try {
          acc.push(FacultyModel.fromJS(faculty))
        } catch (e) {
          // Soft fail when we are unable convert a faculty to a model. This
          // enables us to still render a graph with potentially stale data that
          // has faculty deleted. We should send a message to a monitoring
          // service like Sentry in the future to catch this and delete the
          // faculty in the Lincus data.
          console.error(`Unable to add faculty ${faculty.id}: ${e}`) // eslint-disable-line no-console
        }
        return acc
      }, [])

      // merge new faculty into this.facultyMap
      for (let faculty of this.keywordAnalysis.authors) {
        this.facultyMap.set(faculty.id, faculty)
      }
    })
  }

  @action fetchFacultyHints () {
    if (UiStore.expertiseFilters.search === '') {
      this.facultyHints = []
      return
    }

    getFacultyHints(UiStore.expertiseFilters.search).then(action(response => {
      this.facultyHints = response
    }))
  }

  @action stopFetcher () {
    this.clearKeywordAnalysis()
    this.selectedKeywordId = null
    UiStore.resetExpertiseFilters()

    this.displayResults = false
  }

  constructor () {
    this.fetchKeywordHintsDebounced = debounce(::this.fetchKeywordHints, 200)
    this.fetchFacultyHintsDebounced = debounce(::this.fetchFacultyHints, 200)

    // Set boolean to be true after first search. This isn't a pure fucction
    // since we want the value to be true even after the user has cleared the
    // search bar.
    when(() => {
      if (UiStore.expertiseFilters.search !== '') {
        this.displayResults = true
      }
    })

    reaction(
      () => [ this.selectedKeywordId ],
      () => {
        if (this.selectedKeywordId !== null) {
          this.fetchKeywordAnalysis()
        }
      }
    )
  }
}

const singleton = new ExpertiseStore()
export default singleton



// WEBPACK FOOTER //
// ./resources/assets/js/stores/ExpertiseStore.js